/**
 * modals/ConfluenceModal.tsx
 * Modal for creating/editing confluences
 *
 * Handles confluence creation with name and optional description.
 * Confluences are market factors that can be assigned positive or negative
 * polarity at the playbook level.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { Layers, Plus } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  TextArea,
  Select,
  SelectItem,
  Divider,
  RadioGroup,
  Radio
} from '@enfomentis/ui';
import { CATEGORY_COLORS } from '@/constants';

// 3. Relative imports
import type { ConfluenceDraft, MarketConditionCategoryDraft } from '../hooks/usePlaybookBuilder';

/**
 * Props for ConfluenceModal
 */
interface ConfluenceModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Mode: add or edit */
  mode: 'add' | 'edit';
  /** Existing confluence data (for edit mode) */
  existingData?: ConfluenceDraft | undefined;
  /** Market condition categories */
  marketConditionCategories: MarketConditionCategoryDraft[];
  /** Handler for closing the modal */
  onClose: () => void;
  /** Handler for saving confluence (returns tempId of new item) */
  onSave: (confluenceData: { name: string; description?: string; categoryId?: string }) => string;
  /** Handler for creating a new category */
  onCreateCategory: (name: string, color: string, description?: string) => string;
}

/**
 * Confluence modal component
 */
export const ConfluenceModal: React.FC<ConfluenceModalProps> = ({
  isOpen,
  mode,
  existingData,
  marketConditionCategories,
  onClose,
  onSave,
  onCreateCategory
}) => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    selectedCategoryId: ''
  });

  // Category creation state
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({
    name: '',
    description: '',
    color: CATEGORY_COLORS[0]?.value || '#3b82f6'
  });

  // Initialize form from existing data when in edit mode
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && existingData) {
        setFormData({
          name: existingData.name,
          description: existingData.description || '',
          selectedCategoryId: existingData.categoryId || ''
        });
        setShowCreateCategory(false);
      } else {
        // Reset for add mode
        setFormData({
          name: '',
          description: '',
          selectedCategoryId: ''
        });
        setShowCreateCategory(false);
        setNewCategoryData({
          name: '',
          description: '',
          color: CATEGORY_COLORS[0]?.value || '#3b82f6'
        });
      }
    }
  }, [isOpen, mode, existingData]);

  /**
   * Handle saving the new category
   */
  const handleSaveCategory = (): void => {
    if (!newCategoryData.name.trim()) {
      return; // Category name required
    }

    const categoryId = onCreateCategory(
      newCategoryData.name.trim(),
      newCategoryData.color,
      newCategoryData.description.trim() || undefined
    );

    // Close category creation form and select the new category
    setShowCreateCategory(false);
    setFormData({ ...formData, selectedCategoryId: categoryId });

    // Reset category form
    setNewCategoryData({
      name: '',
      description: '',
      color: CATEGORY_COLORS[0]?.value || '#3b82f6'
    });
  };

  /**
   * Handle form submission
   */
  const handleSave = (): void => {
    if (!formData.name.trim()) {
      return; // Name is required
    }

    const confluenceData: { name: string; description?: string; categoryId?: string } = {
      name: formData.name.trim()
    };

    if (formData.description.trim()) {
      confluenceData.description = formData.description.trim();
    }

    if (formData.selectedCategoryId) {
      confluenceData.categoryId = formData.selectedCategoryId;
    }

    onSave(confluenceData);
    onClose();
  };

  /**
   * Handle cancel
   */
  const handleCancel = (): void => {
    onClose();
  };

  const isNameValid = formData.name.trim().length >= 2;
  const isCategoryNameValid = newCategoryData.name.trim().length >= 2;
  const canSave = isNameValid;
  const canSaveCategory = isCategoryNameValid;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex gap-2 items-center">
              <Layers className="w-5 h-5 text-primary" />
              <span>{mode === 'add' ? 'Add Confluence' : 'Edit Confluence'}</span>
            </ModalHeader>

            <ModalBody className="gap-4">
              {/* Confluence Name */}
              <Input
                label="Confluence Name"
                placeholder="e.g., News Event Within 1 Hour, Against Major Trend, Low Volume"
                value={formData.name}
                onValueChange={(value) => setFormData({ ...formData, name: value })}
                isRequired
                variant="bordered"
                size="lg"
                classNames={{
                  input: "text-base"
                }}
                description="Market factors that influence trade decisions (polarity set per playbook)"
                errorMessage={
                  formData.name.length > 0 && !isNameValid
                    ? "Confluence name must be at least 2 characters"
                    : undefined
                }
                isInvalid={formData.name.length > 0 && !isNameValid}
              />

              {/* Confluence Description */}
              <TextArea
                label="Description"
                placeholder="Optional: Describe how this confluence affects your trade decisions"
                value={formData.description}
                onValueChange={(value) => setFormData({ ...formData, description: value })}
                variant="bordered"
                size="lg"
                classNames={{
                  input: "text-base"
                }}
                minRows={2}
                maxRows={4}
                description="Help yourself remember the details of this market factor"
              />

              <Divider />

              {/* Category Selection OR Create New Category Section */}
              {!showCreateCategory ? (
                <Select
                  label="Category (Optional)"
                  placeholder="Select a category"
                  selectedKeys={formData.selectedCategoryId ? [formData.selectedCategoryId] : []}
                  onSelectionChange={(keys) => {
                    const selectedKey = Array.from(keys)[0] as string;
                    if (selectedKey === '__create_new__') {
                      setShowCreateCategory(true);
                      setFormData({ ...formData, selectedCategoryId: '' });
                    } else {
                      setFormData({ ...formData, selectedCategoryId: selectedKey });
                    }
                  }}
                  variant="bordered"
                  size="lg"
                  description="Choose which category this confluence belongs to"
                >
                  {[
                    ...marketConditionCategories.map((category) => (
                      <SelectItem key={category.tempId} textValue={category.name}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                          <span>{category.name}</span>
                        </div>
                      </SelectItem>
                    )),
                    <SelectItem key="__create_new__" textValue="Create New Category">
                      <div className="flex items-center gap-2 text-primary font-medium">
                        <Plus className="w-4 h-4" />
                        <span>Create New Category</span>
                      </div>
                    </SelectItem>
                  ]}
                </Select>
              ) : (
                <div className="space-y-4 p-4 rounded-lg bg-default-50 border border-default-200">
                  <h4 className="text-sm font-semibold text-text-primary">New Category Details</h4>

                  <Input
                    label="Category Name"
                    placeholder="e.g., Volume, Trend Status, Market Sentiment"
                    value={newCategoryData.name}
                    onValueChange={(value) => setNewCategoryData({ ...newCategoryData, name: value })}
                    isRequired
                    variant="bordered"
                    size="md"
                    classNames={{
                      input: "text-base"
                    }}
                    description="Organize confluences by aspects like: Volume, Timeframe Context, Trend Status, Market Sentiment, Price Action, Risk Metrics"
                    errorMessage={
                      newCategoryData.name.length > 0 && newCategoryData.name.trim().length < 2
                        ? "Category name must be at least 2 characters"
                        : undefined
                    }
                    isInvalid={newCategoryData.name.length > 0 && newCategoryData.name.trim().length < 2}
                  />

                  <TextArea
                    label="Category Description"
                    placeholder="Optional: Describe what this category represents"
                    value={newCategoryData.description}
                    onValueChange={(value) => setNewCategoryData({ ...newCategoryData, description: value })}
                    variant="bordered"
                    size="md"
                    classNames={{
                      input: "text-base"
                    }}
                    minRows={2}
                    maxRows={3}
                  />

                  <div>
                    <label className="text-sm font-medium text-text-primary mb-2 block">
                      Category Color
                    </label>
                    <RadioGroup
                      value={newCategoryData.color}
                      onValueChange={(value) => setNewCategoryData({ ...newCategoryData, color: value })}
                      orientation="horizontal"
                      classNames={{
                        wrapper: "gap-3"
                      }}
                    >
                      {CATEGORY_COLORS.map((color) => (
                        <Radio
                          key={color.value}
                          value={color.value}
                          classNames={{
                            base: "m-0"
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-6 h-6 rounded-full border-2 border-default-300"
                              style={{ backgroundColor: color.value }}
                            />
                            <span className="text-sm">{color.name}</span>
                          </div>
                        </Radio>
                      ))}
                    </RadioGroup>
                  </div>

                  {/* Save Category Button */}
                  <div className="flex gap-2">
                    <Button
                      variant="light"
                      onPress={() => {
                        setShowCreateCategory(false);
                        setNewCategoryData({
                          name: '',
                          description: '',
                          color: CATEGORY_COLORS[0]?.value || '#3b82f6'
                        });
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      color="success"
                      onPress={handleSaveCategory}
                      isDisabled={!canSaveCategory}
                      className="flex-1"
                    >
                      Save Category
                    </Button>
                  </div>
                </div>
              )}
            </ModalBody>

            <ModalFooter>
              <Button
                variant="light"
                onPress={handleCancel}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={handleSave}
                isDisabled={!canSave}
              >
                {mode === 'add' ? 'Add Confluence' : 'Save Changes'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
